/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Children } from 'react';

import type { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { Block, Inline, Text } from '@contentful/rich-text-types';

import { ShowMoreList, typography2 } from '@coursera/cds-core';

import type { EnterpriseArticleLinkFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
import isExternalLink from 'bundles/ent-website/utils/isExternalLink';
import type { SeoContentLinksFragment } from 'bundles/unified-seo-content-common/types';

import _t from 'i18n!nls/shared-articles';

const styles = {
  singleSources: () => css`
    ${typography2.bodyPrimary}
    color: var(--cds-color-neutral-primary);
    font-size: 18px;
    padding-left: 0;
    list-style-position: inside;

    p {
      ${typography2.bodyPrimary}
      font-size: 18px;
      line-height: 32px;
      display: inline;

      a {
        color: #0056d2;
        text-decoration: underline;
      }
    }
  `,
};

type LinksFragment = EnterpriseArticleLinkFragment | SeoContentLinksFragment;

/**
 * Returns a set of custom render nodes for rendering article sources with expandable/collapsible functionality.
 * @param {Function} logArticlesSourcesEvent - Function to log the event when the sources list is expanded/collapsed.
 * @returns {Object} The render nodes for the blocks.
 */
export const getDefaultArticleSourceRenderNodes = (logArticlesSourcesEvent: (isExpanded: boolean) => void) => {
  return {
    [BLOCKS.OL_LIST]: (_: Block | Inline, children: React.ReactNode) => {
      // This is not a direct representation of the amount of nodes in the ordered list, there will be additional validation in contentful to prevent edge cases
      const arrayChildren = Children.toArray(children);
      return arrayChildren.length >= 2 ? (
        <ShowMoreList
          numItemsToShow={1}
          controllerProps={{
            ariaLabel: _t('Show all article sources'),
            itemTypePlural: _t('article sources'),
            expandedLabel: _t('Hide sources'),
            collapsedLabel: _t('View all sources'),
          }}
          onChange={logArticlesSourcesEvent}
        >
          {Children.map(arrayChildren, (child, index) => {
            if (React.isValidElement(child)) {
              return (
                <React.Fragment>
                  {index + 1}.&nbsp;{child.props.children[0]}
                </React.Fragment>
              );
            } else {
              return null;
            }
          })}
        </ShowMoreList>
      ) : (
        <ol css={styles.singleSources}>{children}</ol>
      );
    },
  };
};

/**
 * Returns a set of custom render nodes for rendering rich text in articles.
 * @param {LinksFragment} [links] - The links data for resolving embedded assets.
 * @returns {RenderNode} The render nodes for the blocks and inlines.
 */
export const getDefaultArticlesRichTextRenderNodes = (links?: LinksFragment): RenderNode => {
  return {
    [INLINES.HYPERLINK]: (node: Block | Inline) => {
      return (
        // We set this if the link is external with a ternary

        <a
          href={node.data.uri}
          target={isExternalLink(node.data.uri) ? '_blank' : '_self'}
          rel={isExternalLink(node.data.uri) ? 'noopener noreferrer' : ''}
        >
          {(node.content[0] as Text).value}
        </a>
      );
    },
    /**
     * Renders embedded assets.
     */
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const id = node?.data?.target?.sys?.id;

      const assetMetadata = links?.assets?.block?.find((link) => id === link?.sys?.id);

      const imageSource = assetMetadata?.url;
      const imageTitle = assetMetadata?.title;
      const imageAltText = assetMetadata?.description;

      if (!imageSource || !imageAltText) {
        return null;
      }

      return (
        <figure>
          <img src={imageSource} alt={imageAltText} />
          {imageTitle && <figcaption>{imageTitle}</figcaption>}
        </figure>
      );
    },
  };
};
